import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { observer } from "mobx-react-lite";
import { useFloatingAgentLinkNavigation } from "./floating-agent-link-navigation";
import { exportFloatingAgentThreadEvents } from "./floating-agent-session-events";
import { useFloatingAgentMentionLinkHandler } from "./floating-agent-mention-links";
import { MDSComboboxMention } from "@/mds/components/forms";
import { FloatingAgentPageStore } from "@/store/pages/FloatingAgentPageStore/FloatingAgentPageStore";
import { MDSSession } from "@/mds/components/sessions/session";
import {
  IMDSSessionRef,
  MDSComboboxFetchDataHandlerSessionInputFormContext,
  SessionFeature,
  SessionStatus,
} from "@/mds/components/sessions/session.types";
import {
  FloatingAgentComposerRegistry,
  useComposerMentions,
} from "./floating-agent-composer-mentions";
import { PANEL_THREAD_SLIDE_MS } from "./floating-agent-constants";

/**
 * Slack-style thread pane: the root message pinned first, its replies in
 * order, and a composer that sends into the thread. The server materializes
 * the SessionThread on the first reply. The back affordance lives in the
 * panel header (which reads "Thread" while this pane is open).
 */
export const FloatingAgentThreadView: FC<{
  floatingAgentPage: FloatingAgentPageStore;
  rootEventId: string;
  /** Excludes the panel header and the gap below it. */
  containerHeight: number;
  composerRegistryRef: MutableRefObject<FloatingAgentComposerRegistry>;
  /** The main window's foregrounded notes, attached as implicit mentions. */
  foregroundMentions: MDSComboboxMention[];
  /**
   * A tapped thread peek lands ON its reply; positions the list's first render.
   * Latched by the panel when the thread opens.
   */
  scrollToEventId?: string;
  /** Anchors the "New" divider. */
  unreadDividerEventId?: string;
  /** Dissolves the divider in place once the reader has caught up. */
  isUnreadDividerRetired?: boolean;
  /** Engaging this composer, or settling at the bottom, retires the divider. */
  onRetireUnreadDivider?: () => void;
  /** Reports composer focus so the panel can hide chrome around the keyboard. */
  onComposerFocusChanged?: (isFocused: boolean) => void;
  /** False on phone web, where focusing would pop the on-screen keyboard. */
  autoFocusComposer: boolean;
}> = observer(
  ({
    floatingAgentPage,
    rootEventId,
    containerHeight,
    composerRegistryRef,
    foregroundMentions,
    scrollToEventId,
    unreadDividerEventId,
    isUnreadDividerRetired,
    onRetireUnreadDivider,
    onComposerFocusChanged,
    autoFocusComposer,
  }) => {
    const theme = useTheme();
    const sessionRef = useRef<IMDSSessionRef>(null);
    const handleClickLink = useFloatingAgentLinkNavigation();
    const handleMentionLink = useFloatingAgentMentionLinkHandler();

    const rootEvent = floatingAgentPage.sessionEvents.find(
      (event) => event.id === rootEventId,
    );
    const replies = floatingAgentPage.threadReplies(rootEventId);

    useEffect(() => {
      if (!rootEvent) return;
      sessionRef.current?.upsertEvents(
        exportFloatingAgentThreadEvents(rootEvent, replies),
      );
    }, [rootEvent, replies]);

    const { comboboxProps, clearMentions, buildContext } = useComposerMentions({
      externalMentions: foregroundMentions,
    });

    // Register this pane's composer for PTT sends: the thread pane is mounted
    // only while open, so its registration marks it as the visible composer,
    // and PTT replies into this thread like the pane's typed sends do.
    useEffect(() => {
      composerRegistryRef.current.thread = {
        buildContext,
        clearMentions,
        getReplyTo: () => ({
          eventId: rootEventId,
          sessionThreadId: floatingAgentPage.threadIdForRoot(rootEventId),
        }),
      };
      return () => {
        composerRegistryRef.current.thread = null;
      };
    }, [
      composerRegistryRef,
      buildContext,
      clearMentions,
      floatingAgentPage,
      rootEventId,
    ]);

    const handleNativeChatLimitClick = useCallback(() => {
      void floatingAgentPage.openNativeChatLimitUpsellForCurrentGate();
    }, [floatingAgentPage]);
    const sessionComboboxProps = useMemo(
      () => ({
        ...comboboxProps,
        isDisabled: floatingAgentPage.isNativeChatMessageInputDisabled,
        onDisabledClick: handleNativeChatLimitClick,
        onInputFocus: () => onComposerFocusChanged?.(true),
        onInputBlur: () => onComposerFocusChanged?.(false),
        // Typing, opening the mention picker, and adding a chip all count as
        // starting a reply.
        onContentModified: onRetireUnreadDivider,
        onLeftAddonButtonClick: onRetireUnreadDivider,
        onMentionAdd: (mention: MDSComboboxMention) => {
          onRetireUnreadDivider?.();
          comboboxProps.onMentionAdd(mention);
        },
      }),
      [
        comboboxProps,
        floatingAgentPage.isNativeChatMessageInputDisabled,
        handleNativeChatLimitClick,
        onRetireUnreadDivider,
        onComposerFocusChanged,
      ],
    );

    const handleInputFormSubmit = useCallback(
      async ({
        content,
        context,
      }: {
        content: string;
        context: MDSComboboxFetchDataHandlerSessionInputFormContext;
      }) => {
        const sent = await floatingAgentPage.sendUserMessage({
          content,
          source: "text",
          context,
          replyTo: {
            eventId: rootEventId,
            sessionThreadId: floatingAgentPage.threadIdForRoot(rootEventId),
          },
        });
        if (sent) clearMentions();
        return sent;
      },
      [floatingAgentPage, rootEventId, clearMentions],
    );

    return (
      <StyledThreadPane>
        <MDSSession
          ref={sessionRef}
          sessionTitle="Thread"
          sessionSubtitle=""
          initialSessionEvents={[]}
          initialSessionStatus={SessionStatus.Initial}
          containerHeight={containerHeight}
          isOpen
          comboboxProps={sessionComboboxProps}
          config={{
            openLinksInNewTab: false,
            features: [SessionFeature.AllowCopy],
            linkHandler: handleMentionLink,
            onClickLink: handleClickLink,
            // The thread pane slides in already-visible; no fade needed.
            disableListEnterAnimation: true,
            // Chrome below the list (composer margins). containerHeight already
            // excludes the panel header, so counting it here too would size the
            // list against a basis taller than the pane and slide the last
            // replies under the composer.
            staticViewChromeHeightPx: 16,
            // Matches the main pane: replies fade into the panel behind the
            // composer instead of ending on a hard edge.
            staticViewComposerFadeColor: theme.colors.surface.chat,
            staticViewHeightBasis: `${containerHeight}px`,
            initialScrollToEventId: scrollToEventId,
            unreadDividerEventId,
            isUnreadDividerRetired,
            onCaughtUpNearBottom: onRetireUnreadDivider,
            showJumpToLatest: true,
            unreadCount: isUnreadDividerRetired
              ? 0
              : floatingAgentPage.unreadThreadCountFromAnchor(rootEventId, unreadDividerEventId),
            autoFocusComposer:
              autoFocusComposer && !floatingAgentPage.isNativeChatMessageInputDisabled,
            // The pane slides in over PANEL_THREAD_SLIDE_MS; focus after it
            // settles so the browser doesn't scroll the animating track.
            autoFocusComposerDelayMs: PANEL_THREAD_SLIDE_MS,
          }}
          handlers={{
            handleViewModeChanged: () => {},
            handleCountdownCompleted: () => {},
            handleFetchComboboxData: floatingAgentPage.handleComboboxFetch,
            handleInputFormSubmit,
          }}
        />
      </StyledThreadPane>
    );
  },
);

const StyledThreadPane = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`;
